import { Switch, Route, Redirect, match } from "react-router-dom";

import { makeStyles } from "@material-ui/core";

import LoggedInAdminProvider from "src/components/LoggedInAdminProvider";
import Admins from "src/pages/Admins";
import CampaignDetails from "src/pages/CampaignDetails";
import CampaignList from "src/pages/CampaignList";
import { useFeatureFlag } from "src/util/featureFlags";

import OrgSidebar from "./components/OrgSidebar";
import SubaccountProvider, {
  useSubaccount
} from "./components/SubaccountProvider";
import SubaccountSidebar from "./components/SubaccountSidebar";
import TopBar from "./components/TopBar";
import UserSidebar from "./components/UserSidebar";
import BlockedPhoneNumbers from "./pages/BlockedPhoneNumbers";
import Integrations from "./pages/Integrations";
import OptOuts from "./pages/OptOuts";
import OrgExports from "./pages/OrgExports";
import DataDictionary from "./pages/OrgExports/DataDictionary";
import OrgOwners from "./pages/OrgOwners";
import OrgSettings from "./pages/OrgSettings";
import OrgSubaccounts from "./pages/OrgSubaccounts";
import OrgUsage from "./pages/OrgUsage";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import {
  StaffSidebar,
  StaffTenantAdmin,
  StaffEmailAdmin
} from "./pages/StaffAdmin";
import SubaccountUsage from "./pages/SubaccountUsage";
import Subaccounts from "./pages/Subaccounts";

const useStyles = makeStyles(() => ({
  pageWrapper: {
    flex: "1"
  },
  contentWrapper: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
    paddingTop: 80
  }
}));

function SubaccountTopBar() {
  const { subaccount, subaccountLink } = useSubaccount()!;
  return (
    <TopBar title={subaccount.name} titleLink={subaccountLink("/campaigns")} />
  );
}

function SubaccountRouter({
  match
}: {
  match: match<{ subaccountId?: string }>;
}) {
  const path = (p: string) => `${match.path}${p}`;
  const classes = useStyles();
  const phoneVerificationEnabled = useFeatureFlag("callerPhoneVerification");

  return (
    <SubaccountProvider
      explicitSubaccountId={match.params.subaccountId ?? null}
    >
      <SubaccountTopBar />
      <div className={classes.contentWrapper}>
        <SubaccountSidebar baseUrl={match.url} />
        <div className={classes.pageWrapper}>
          <Switch>
            <Route path={path("/campaigns")} component={CampaignList} exact />
            <Route
              path={path("/campaigns/:id/:section")}
              component={CampaignDetails}
            />
            <Route path={path("/integrations")} component={Integrations} />
            <Route path={path("/admins")} component={Admins} />
            <Route path={path("/settings")} component={Settings} exact />
            <Route
              path={path("/settings/data-dictionary")}
              component={DataDictionary}
            />
            <Route path={path("/do-not-call-list")} component={OptOuts} />
            {phoneVerificationEnabled && (
              <Route
                path={path("/blocked-callers")}
                component={BlockedPhoneNumbers}
              />
            )}
            <Route path={path("/usage")} component={SubaccountUsage} />
            <Redirect to={path("/campaigns")} />
          </Switch>
        </div>
      </div>
    </SubaccountProvider>
  );
}

function UserRouter({ match }: { match: match<{ subaccountId?: string }> }) {
  const classes = useStyles();
  const path = (p: string) => `${match.path}${p}`;

  return (
    <>
      <TopBar title="User Settings" titleLink={path("/profile")} />
      <div className={classes.contentWrapper}>
        <UserSidebar baseUrl={match.url} />
        <div className={classes.pageWrapper}>
          <Switch>
            <Route path={path("/profile")} component={Profile} />
            <Route path={path("/subaccounts")} component={Subaccounts} />
            <Redirect to={path("/profile")} />
          </Switch>
        </div>
      </div>
    </>
  );
}

function OrganizationRouter({
  match
}: {
  match: match<{ subaccountId?: string }>;
}) {
  const classes = useStyles();
  const path = (p: string) => `${match.path}${p}`;

  return (
    <>
      <TopBar title="Organization Settings" titleLink={path("/settings")} />
      <div className={classes.contentWrapper}>
        <OrgSidebar baseUrl={match.url} />
        <div className={classes.pageWrapper}>
          <Switch>
            <Route path={path("/settings")} component={OrgSettings} />
            <Route path={path("/subaccounts")} component={OrgSubaccounts} />
            <Route path={path("/owners")} component={OrgOwners} />
            <Route path={path("/exports")} component={OrgExports} exact />
            <Route
              path={path("/exports/data-dictionary")}
              component={DataDictionary}
            />
            <Route path={path("/usage")} component={OrgUsage} />
            <Redirect to={path("/settings")} />
          </Switch>
        </div>
      </div>
    </>
  );
}

function StaffRouter() {
  const classes = useStyles();
  return (
    <>
      <TopBar title="STW Staff Admin" titleLink={"/staff"} />
      <div className={classes.contentWrapper}>
        <StaffSidebar />
        <div className={classes.pageWrapper}>
          <Switch>
            <Route path="/staff/tenants" component={StaffTenantAdmin} />
            <Route path="/staff/emails" component={StaffEmailAdmin} />
            <Redirect to="/staff/tenants" />
          </Switch>
        </div>
      </div>
    </>
  );
}

export default function AppRouter(): JSX.Element {
  return (
    <LoggedInAdminProvider>
      <Switch>
        <Route path="/admin" component={SubaccountRouter} />
        <Route path="/subaccounts/:subaccountId" component={SubaccountRouter} />
        <Route path="/user" component={UserRouter} />
        <Route path="/organization" component={OrganizationRouter} />
        <Route path="/staff" component={StaffRouter} />
        <Redirect to="/admin" />
      </Switch>
    </LoggedInAdminProvider>
  );
}
